// src/features/feeds/components/FeedGrid.tsx

import { useRef, useCallback, useMemo, useState, useEffect, useLayoutEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Box, Checkbox, Menu, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconRss,
  IconChevronUp,
  IconChevronDown,
  IconCode,
  IconPlayerPlay,
  IconPlayerPause,
  IconTrash,
} from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { FeedGridRow as FeedGridRowComponent } from './FeedGridRow';
import { useSelection } from '@/shared/contexts/SelectionContext';
import {
  FEED_ROW_HEIGHT,
  FEED_GRID_MAX_VIEWPORT_HEIGHT_PX,
  FEED_GRID_MAX_VIRTUAL_ROWS,
  FEED_VIRTUALIZER_OVERSCAN,
  FEED_HEADER_COLUMNS,
  type FeedHeaderColumn,
} from '@/lib/constants/feed';
import type { FeedGridRow, FeedSortKey } from '@/lib/types/Feed';
import type { SortDirection } from '@/lib/types/common';
import styles from './FeedGrid.module.css';

interface FeedGridProps {
  count: number;
  allIds: number[];
  getRow: (index: number) => FeedGridRow | null;
  sortKey: FeedSortKey;
  sortDirection: SortDirection;
  onSort: (key: FeedSortKey) => void;
  onPublish?: (feed: FeedGridRow) => void;
  onActivateToggle?: (feed: FeedGridRow) => void;
  onDelete?: (feed: FeedGridRow) => void;
  initialScrollTop?: number;
  onScrollTopChange?: (scrollTop: number) => void;
  onFirstVisibleRowsPaint?: () => void;
}

export function FeedGrid({
  count,
  allIds,
  getRow,
  sortKey,
  sortDirection,
  onSort,
  onPublish,
  onActivateToggle,
  onDelete,
  initialScrollTop = 0,
  onScrollTopChange,
  onFirstVisibleRowsPaint,
}: FeedGridProps) {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const didRestoreScrollRef = useRef(false);
  const lastPaintTokenRef = useRef<string | null>(null);

  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === 'undefined') return 720;
    const estimated = Math.max(0, window.innerHeight - 220);
    return Math.min(estimated, FEED_GRID_MAX_VIEWPORT_HEIGHT_PX);
  });

  const { selectedIds, isSelected, selectOnly, toggle, toggleRange, addToSelection, removeFromSelection } = useSelection();

  const allIdsRef = useRef<number[]>([]);
  allIdsRef.current = allIds;

  const virtualizer = useVirtualizer({
    count,
    // Seed the virtualizer's internal offset with the restored scroll position.
    // Its attach step syncs the element to that internal offset, so without this
    // a re-attach (e.g. StrictMode's dev double-mount) scrolls back to 0 and
    // undoes the scroll restore below.
    initialOffset: initialScrollTop,
    getScrollElement: () => parentRef.current,
    estimateSize: () => FEED_ROW_HEIGHT,
    overscan: FEED_VIRTUALIZER_OVERSCAN,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const renderedVirtualRows = virtualRows.length > FEED_GRID_MAX_VIRTUAL_ROWS
    ? virtualRows.slice(0, FEED_GRID_MAX_VIRTUAL_ROWS)
    : virtualRows;

  // Viewport height measurement
  useLayoutEffect(() => {
    const containerEl = containerRef.current;
    const headerEl = headerRef.current;
    if (!containerEl || !headerEl || typeof ResizeObserver === 'undefined') return;

    const updateViewportHeight = () => {
      const measured = Math.max(0, containerEl.clientHeight - headerEl.offsetHeight);
      const fallback = typeof window !== 'undefined'
        ? Math.max(0, window.innerHeight - headerEl.offsetHeight - 140)
        : measured;
      const bounded = Math.min(
        measured > 0 ? measured : fallback,
        FEED_GRID_MAX_VIEWPORT_HEIGHT_PX,
      );
      setViewportHeight((prev) => (Math.abs(prev - bounded) < 1 ? prev : bounded));
    };

    updateViewportHeight();
    const observer = new ResizeObserver(() => updateViewportHeight());
    observer.observe(containerEl);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, []);

  // Scroll restoration
  useLayoutEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || didRestoreScrollRef.current) return;
    if (initialScrollTop > 0) {
      scrollEl.scrollTop = initialScrollTop;
      virtualizer.scrollToOffset(initialScrollTop);
    }
    didRestoreScrollRef.current = true;
  }, [initialScrollTop, virtualizer]);

  // Scroll tracking
  useEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || !onScrollTopChange) return;
    const handleScroll = () => onScrollTopChange(scrollEl.scrollTop);
    handleScroll();
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [onScrollTopChange]);

  // First visible rows paint tracking
  useEffect(() => {
    if (count === 0 || renderedVirtualRows.length === 0) return;
    const paintToken = `${sortKey}:${sortDirection}:${count}`;
    if (lastPaintTokenRef.current === paintToken) return;
    lastPaintTokenRef.current = paintToken;
    onFirstVisibleRowsPaint?.();
  }, [count, renderedVirtualRows.length, sortKey, sortDirection, onFirstVisibleRowsPaint]);

  const handleRangeSelect = useCallback(
    (id: number, index: number) => toggleRange(id, index, allIdsRef.current),
    [toggleRange],
  );

  const handleNavigate = useCallback(
    (href: string) => navigate({ to: href }),
    [navigate],
  );

  // --- Shared action menu state (single Menu instance for all rows) ---
  const [menuState, setMenuState] = useState<{
    opened: boolean;
    rowIndex: number | null;
  }>({ opened: false, rowIndex: null });

  const anchorRef = useRef<HTMLDivElement>(null);

  const handleMenuOpen = useCallback((index: number, buttonEl: HTMLElement) => {
    const rect = buttonEl.getBoundingClientRect();
    if (anchorRef.current) {
      anchorRef.current.style.position = 'fixed';
      anchorRef.current.style.top = `${rect.top}px`;
      anchorRef.current.style.left = `${rect.left}px`;
      anchorRef.current.style.width = `${rect.width}px`;
      anchorRef.current.style.height = `${rect.height}px`;
    }
    setMenuState({ opened: true, rowIndex: index });
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuState(prev => ({ ...prev, opened: false }));
  }, []);

  const handlePublishRow = useCallback((feed: FeedGridRow) => {
    onPublish?.(feed);
  }, [onPublish]);

  // Close menu on scroll
  useEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || !menuState.opened) return;
    const onScroll = () => handleMenuClose();
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, [menuState.opened, handleMenuClose]);

  const activeFeed = menuState.rowIndex !== null ? getRow(menuState.rowIndex) : null;

  // Delete confirmation
  const handleRowDelete = useCallback((feed: FeedGridRow) => {
    modals.openConfirmModal({
      title: 'Delete Feed',
      children: (
        <Text size="sm">
          Are you sure you want to delete &ldquo;{feed.title}&rdquo;? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => onDelete?.(feed),
    });
  }, [onDelete]);

  // Header checkbox scoped to the VISIBLE (filtered) rows — select-all under a
  // search must not drop selections made under a previous search. Memoized so
  // scroll re-renders don't pay O(n).
  const visibleSelectedCount = useMemo(() => {
    if (selectedIds.size === 0) return 0;
    let n = 0;
    for (const id of allIds) {
      if (selectedIds.has(id)) n++;
    }
    return n;
  }, [allIds, selectedIds]);

  const allSelected = count > 0 && visibleSelectedCount === count;
  const someSelected = visibleSelectedCount > 0 && visibleSelectedCount < count;

  const handleHeaderCheckbox = () => {
    if (allSelected) {
      removeFromSelection(allIdsRef.current);
    } else {
      addToSelection(allIdsRef.current);
    }
  };

  const handleHeaderClick = (column: FeedHeaderColumn) => {
    if (column.sortable && column.sortKey) {
      onSort(column.sortKey);
    }
  };

  // Empty state
  if (count === 0) {
    return (
      <Box ref={containerRef} className={styles.gridContainer}>
        <Box className={styles.emptyState}>
          <IconRss size={48} stroke={1.5} opacity={0.5} />
          <Text size="lg" mt="md" fw={500}>No feeds found</Text>
          <Text size="sm" c="dimmed" mt="xs">
            Try adjusting your search or create a new feed
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} className={styles.gridContainer}>
      {/* Header row using CSS Grid */}
      <div ref={headerRef} className={`${styles.header} ${styles.gridColumns}`}>
        {FEED_HEADER_COLUMNS.map((col) => {
          if (col.key === 'checkbox') {
            return (
              <div key={col.key} className={`${styles.headerCell} ${styles.cellCheckbox}`}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleHeaderCheckbox}
                  aria-label="Select all"
                />
              </div>
            );
          }

          const isActive = col.sortable && col.sortKey === sortKey;

          return (
            <div
              key={col.key}
              className={`${styles.headerCell} ${col.sortable ? styles.sortableHeader : ''}`}
              style={{
                justifyContent: col.align === 'right' ? 'flex-end' :
                                col.align === 'center' ? 'center' : 'flex-start',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={() => handleHeaderClick(col)}
            >
              {col.label}
              {col.sortable && (
                <span className={`${styles.sortIcon} ${isActive ? styles.sortIconActive : ''}`}>
                  {isActive && sortDirection === 'asc' ? (
                    <IconChevronUp size={14} />
                  ) : isActive && sortDirection === 'desc' ? (
                    <IconChevronDown size={14} />
                  ) : (
                    <IconChevronDown size={14} style={{ opacity: 0.3 }} />
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Scrollable body with virtualization */}
      <div
        ref={parentRef}
        className={styles.body}
        style={viewportHeight > 0 ? { height: `${viewportHeight}px` } : undefined}
      >
        <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
          {renderedVirtualRows.map(virtualRow => {
            const feed = getRow(virtualRow.index);
            if (!feed) return null;

            const selected = isSelected(feed.feedId);

            return (
              <FeedGridRowComponent
                key={virtualRow.key}
                feed={feed}
                index={virtualRow.index}
                isSelected={selected}
                onSelectOnly={selectOnly}
                onToggle={toggle}
                onRangeSelect={handleRangeSelect}
                onMenuOpen={handleMenuOpen}
                onNavigate={handleNavigate}
                onPublish={handlePublishRow}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Single shared action menu */}
      <Menu
        opened={menuState.opened}
        onChange={(opened) => { if (!opened) handleMenuClose(); }}
        shadow="md"
        width={200}
        position="bottom-end"
      >
        <Menu.Target>
          <div ref={anchorRef} style={{ position: 'fixed', pointerEvents: 'none', width: 0, height: 0 }} />
        </Menu.Target>
        <Menu.Dropdown>
          {activeFeed && (
            <>
              <Menu.Item
                leftSection={<IconCode size={14} />}
                onClick={() => { handleMenuClose(); onPublish?.(activeFeed); }}
                disabled={!activeFeed.isActive}
              >
                Publish
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={activeFeed.isActive
                  ? <IconPlayerPause size={14} />
                  : <IconPlayerPlay size={14} />}
                onClick={() => { handleMenuClose(); onActivateToggle?.(activeFeed); }}
              >
                {activeFeed.isActive ? 'Deactivate' : 'Activate'}
              </Menu.Item>
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={() => { handleMenuClose(); handleRowDelete(activeFeed); }}
              >
                Delete
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}
